import { Head, Link, router } from "@inertiajs/react"
import { AlertTriangle, Loader2, PiggyBank } from "lucide-react"
import { useState } from "react"

import { formatDate } from "@/components/products/work_orders/status-chip"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Progress } from "@/components/ui/progress"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { useOrgRoutes } from "@/hooks/use-org-routes"
import AppLayout from "@/layouts/app-layout"
import { cn } from "@/lib/utils"
import type { BreadcrumbItem, RequalBurnDown } from "@/types"

interface RequalCar {
  id: number
  mark_number: string
  car_type: string | null
  fleet_code: string | null
  facility_name: string | null
  lessor_name: string | null
  rider_number: string | null
  rider_expiration_on: string | null
  rider_monthly_rate_cents: number | null
  next_qual_due_on: string | null
  /** rider expires before the qual comes due — money burned if we test it. */
  conflict: boolean
  status: "not_started" | "in_flight" | "complete"
  work_order_id: number | null
  deferred: boolean
}

interface Props {
  program_year: number
  available_years: number[]
  burn_down: RequalBurnDown
  pace: { expected: number; ahead_behind: number; fraction_elapsed: number }
  cars: RequalCar[]
  suggested_saving_cents: number | null
  can_edit: boolean
}

/**
 * The requalification program: will we finish the year, and which of these cars
 * should we not be testing at all.
 *
 * The second question is the one that pays for the software. A car whose rider
 * expires before its qual comes due is compliance the lessee will never use —
 * the conflict rows are highlighted and carry the action that turns each one
 * into a line in the savings ledger.
 */
export default function RequalIndex({
  program_year,
  available_years,
  burn_down,
  pace,
  cars,
  suggested_saving_cents,
  can_edit,
}: Props) {
  const routes = useOrgRoutes()
  const [skipping, setSkipping] = useState<RequalCar | null>(null)

  const breadcrumbs: BreadcrumbItem[] = [
    { title: "Dashboard", href: routes.dashboardPath() },
    { title: "Requal Program", href: routes.requalPath() },
  ]

  const conflicts = cars.filter((car) => car.conflict && !car.deferred)
  const pct =
    burn_down.target > 0
      ? Math.round((burn_down.complete / burn_down.target) * 100)
      : 0

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Requal Program" />

      <div className="space-y-5 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Requal Program</h1>
            <p className="text-muted-foreground mt-1 text-sm">
              Burn-down against the year&apos;s target, and the cars worth
              skipping.
            </p>
          </div>
          <Select
            value={String(program_year)}
            onValueChange={(year) =>
              router.get(routes.requalPath(), { program_year: year })
            }
          >
            <SelectTrigger className="w-32" aria-label="Program year">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {available_years.map((year) => (
                <SelectItem key={year} value={String(year)}>
                  {year}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
          <Tile label="Target" value={burn_down.target} />
          <Tile label="Completed" value={burn_down.complete} />
          <Tile
            label="In flight"
            value={burn_down.in_progress + burn_down.planned}
          />
          <Tile label="Remaining" value={burn_down.remaining} />
          <Tile
            label={pace.ahead_behind >= 0 ? "Ahead of pace" : "Behind pace"}
            value={`${pace.ahead_behind >= 0 ? "+" : ""}${pace.ahead_behind}`}
            tone={pace.ahead_behind >= 0 ? "good" : "bad"}
            hint={`${pace.expected} expected by now`}
          />
        </div>

        <Card>
          <CardContent className="space-y-2 pt-6">
            <div className="flex justify-between text-sm">
              <span className="font-medium">
                {burn_down.complete} of {burn_down.target} complete
              </span>
              <span className="text-muted-foreground tabular-nums">{pct}%</span>
            </div>
            <Progress value={pct} />
            <p className="text-muted-foreground text-xs">
              {Math.round(pace.fraction_elapsed * 100)}% of {program_year}{" "}
              elapsed.
            </p>
          </CardContent>
        </Card>

        {conflicts.length > 0 && (
          <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700/60 dark:bg-amber-950/40">
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
              <AlertTriangle className="size-4" />
              {conflicts.length} car{conflicts.length === 1 ? "" : "s"} would be
              requalified after the rider expires — testing them buys compliance
              the lease will never use.
            </p>
          </div>
        )}

        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Car</TableHead>
                <TableHead>Fleet</TableHead>
                <TableHead>Facility</TableHead>
                <TableHead>Lessor / rider</TableHead>
                <TableHead>Qual due</TableHead>
                <TableHead>Rider expires</TableHead>
                <TableHead>Status</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {cars.length === 0 && (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="text-muted-foreground py-10 text-center text-sm"
                  >
                    No cars are due in {program_year}.
                  </TableCell>
                </TableRow>
              )}
              {cars.map((car) => (
                <TableRow
                  key={car.id}
                  className={cn(
                    car.conflict &&
                      !car.deferred &&
                      "bg-amber-50 dark:bg-amber-950/30",
                  )}
                >
                  <TableCell className="font-medium">
                    <Link
                      href={routes.carPath(car.id)}
                      className="hover:underline"
                    >
                      {car.mark_number}
                    </Link>
                  </TableCell>
                  <TableCell className="text-sm">
                    {car.fleet_code ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {car.facility_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm">
                    {car.lessor_name ?? "—"}
                    {car.rider_number && (
                      <span className="text-muted-foreground">
                        {" "}
                        · {car.rider_number}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm tabular-nums">
                    {formatDate(car.next_qual_due_on)}
                  </TableCell>
                  <TableCell
                    className={cn(
                      "text-sm tabular-nums",
                      car.conflict && "text-amber-700 dark:text-amber-400",
                    )}
                  >
                    {formatDate(car.rider_expiration_on)}
                  </TableCell>
                  <TableCell>
                    <StatusCell car={car} />
                  </TableCell>
                  <TableCell className="text-right">
                    {can_edit && car.conflict && !car.deferred && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSkipping(car)}
                      >
                        <PiggyBank className="mr-1 size-4" />
                        Skip &amp; log savings
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <SkipDialog
        car={skipping}
        programYear={program_year}
        suggestedCents={suggested_saving_cents}
        savingsPath={routes.savingsPath()}
        onClose={() => setSkipping(null)}
      />
    </AppLayout>
  )
}

function StatusCell({ car }: { car: RequalCar }) {
  const routes = useOrgRoutes()

  if (car.deferred) return <Badge variant="secondary">Skipped</Badge>
  if (car.status === "complete")
    return (
      <Badge className="border-emerald-400 bg-emerald-100 text-emerald-900 dark:border-emerald-700/60 dark:bg-emerald-950/50 dark:text-emerald-200">
        Complete
      </Badge>
    )
  if (car.status === "in_flight" && car.work_order_id)
    return (
      <Link
        href={routes.workOrderPath(car.work_order_id)}
        className="hover:underline"
      >
        <Badge variant="outline">In flight</Badge>
      </Link>
    )
  return <span className="text-muted-foreground text-sm">Not started</span>
}

function Tile({
  label,
  value,
  tone,
  hint,
}: {
  label: string
  value: number | string
  tone?: "good" | "bad"
  hint?: string
}) {
  return (
    <Card>
      <CardContent className="pt-6">
        <p className="text-muted-foreground text-xs">{label}</p>
        <p
          className={cn(
            "mt-1 text-2xl font-semibold tabular-nums",
            tone === "good" && "text-emerald-600 dark:text-emerald-400",
            tone === "bad" && "text-destructive",
          )}
        >
          {value}
        </p>
        {hint && <p className="text-muted-foreground mt-0.5 text-xs">{hint}</p>}
      </CardContent>
    </Card>
  )
}

/**
 * Skipping is a financial claim, so it asks for the number rather than assuming
 * one. The suggestion is what a requal has actually cost this tenant — an
 * invented figure would make the whole ROI ledger unciteable.
 */
function SkipDialog({
  car,
  programYear,
  suggestedCents,
  savingsPath,
  onClose,
}: {
  car: RequalCar | null
  programYear: number
  suggestedCents: number | null
  savingsPath: string
  onClose: () => void
}) {
  const [amount, setAmount] = useState("")
  const [description, setDescription] = useState("")
  const [saving, setSaving] = useState(false)

  const suggestion =
    suggestedCents != null
      ? (suggestedCents / 100).toFixed(2)
      : car?.rider_monthly_rate_cents != null
        ? (car.rider_monthly_rate_cents / 100).toFixed(2)
        : ""

  function submit() {
    if (saving || !car) return
    const dollars = Number(amount || suggestion)
    if (!Number.isFinite(dollars) || dollars <= 0) return

    setSaving(true)
    router.post(
      savingsPath,
      {
        savings_ledger_entry: {
          category: "avoided_requal",
          amount_cents: Math.round(dollars * 100),
          car_id: car.id,
          description:
            description ||
            `Requal skipped — rider ${car.rider_number ?? ""} expires ${car.rider_expiration_on ?? "before the qual is due"}`,
        },
        defer_requal: 1,
        program_year: programYear,
      },
      {
        onSuccess: () => {
          setAmount("")
          setDescription("")
          onClose()
        },
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <Dialog open={car != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Skip requal on {car?.mark_number}</DialogTitle>
          <DialogDescription>
            The rider expires {formatDate(car?.rider_expiration_on)}, before the
            qual is due {formatDate(car?.next_qual_due_on)}. Logging this
            records the avoided cost against the car.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="skip-amount">Amount saved (USD)</Label>
            <Input
              id="skip-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              placeholder={suggestion || "Enter an amount"}
              onChange={(event) => setAmount(event.target.value)}
            />
            {suggestion && (
              <p className="text-muted-foreground text-xs">
                Suggested ${suggestion} — the average cost of a completed requal
                here.
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="skip-note">Note</Label>
            <Textarea
              id="skip-note"
              rows={2}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder="Optional — why this one was skipped"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={saving || !(amount || suggestion)}>
            {saving && <Loader2 className="mr-1 size-4 animate-spin" />}
            Log savings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
